import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

type UseListSearchParamOptions = {
  // The name of the search param you want to use
  param: string

  // If you want to set a default value, pass it in here
  defaultValue?: string

  // If you want to clear other search params when this one changes, pass them in here
  fieldsToClearOnChange?: string[]
}

export default function useStatefulSearchParam({
  param,
  defaultValue,
  fieldsToClearOnChange,
}: UseListSearchParamOptions) {
  const [searchParams, setSearchParams] = useSearchParams()
  const value = searchParams.get(param) ?? defaultValue

  const toggleValue = useCallback(
    (newValue: string, replace = true) => {
      const allCurrentSearchParams = Object.fromEntries(searchParams.entries())
      const fieldsToClear = fieldsToClearOnChange ?? []
      fieldsToClear.forEach((field) => {
        delete allCurrentSearchParams[field]
      })
      if (newValue) {
        setSearchParams(
          { ...allCurrentSearchParams, [param]: newValue },
          { replace },
        )
      } else {
        setSearchParams(
          { ...allCurrentSearchParams, [param]: 'none' },
          { replace },
        )
      }
    },
    [param, searchParams, setSearchParams, fieldsToClearOnChange],
  )

  const resetValue = useCallback(
    (replace = true) => {
      const allCurrentSearchParams = Object.fromEntries(searchParams.entries())
      delete allCurrentSearchParams[param]
      setSearchParams(allCurrentSearchParams, { replace })
    },
    [param, searchParams, setSearchParams],
  )

  const isInUrl = searchParams.has(param)

  return useMemo(
    () => ({
      value,
      toggleValue,
      resetValue,
      isInUrl,
    }),
    [value, toggleValue, resetValue, isInUrl],
  )
}
